import {
    DeleteFranchisePartnerRequestParams,
    DeleteFranchisePartnerUserRequest,
    GetFranchisePartnerConsolidatedReportRequestParams,
    GetFranchisePartnerLocationRequestParams,
    GetFranchisePartnerLocationsRequestParams,
    GetFranchisePartnerRequestParams,
    GetFranchisePartnerUsersRequestParams,
    UpdateFranchisePartnerLocationRequestParams,
    UpdateFranchisePartnerRequestParams,
    UpdateFranchisePartnerUserRequest
} from 'modules/franchise_partners/types';
import { GetLocationRequestParams, UpdateLocationRequestParams } from 'modules/locations/types';
import {
    DeleteLocationGroupRequestParams,
    GetLocationGroupRequestParams,
    UpdateLocationGroupRequestParams
} from 'modules/location_groups/types';
import { GetConsolidatedReportRequest, GetDailyReportRequest } from 'modules/reports/types';
import { DeleteUserRequestParams, GetUserRequestParams, UpdateUserRequestParams } from 'modules/users/types';
import { DeleteIngredientRequestParams, UpdateIngredientRequestParams } from '../modules/products/types';
import { DeleteDeliveryScheduleRequestParams } from 'modules/delivery_schedules/types';

/**
 * Object that defines routes for different sections of the application.
 * @constant
 * @property {Object} auth - Routes related to authentication.
 * @property {string} auth.login - Route for login page.
 * @property {string} auth.googleRedirect - Route for Google auth redirect callback.
 * @property {string} fallback - Route to fallback in case the requested route is not found.
 */
export const APPLICATION_ROUTES = {
    auth: {
        login: '/login',
        googleRedirect: '/auth/google/callback'
    },
    dashboard: '/report/dashboard',
    consolidatedReport: '/report/production',
    products: '/ingredients',
    dailyReport: '/report/daily',
    operationalPLReport: '/report/operational-pl',
    users: { list: '/users', detail: '/user/:id', create: '/users/create' },
    locations: { list: '/locations', detail: '/location/:id' },
    locationGroupSettings: '/location-group-settings',
    coreProducts: '/core-products',
    categoryManagement: '/category-management',
    franchisePartners: {
        list: '/franchise_partners',
        detail: '/franchise_partners/:id',
        create: '/franchise_partners/create'
    },
    ordering: {
        browse: '/ordering',
        checkout: '/ordering/checkout',
        confirmation: '/ordering/confirmation',
        status: '/ordering/status',
        statusOrderDetail: '/ordering/status/order/:orderId',
        statusOrderEdit: '/ordering/status/order/:orderId/edit',
        receiptingList: '/ordering/receipting',
        receiptingDetail: '/ordering/receipting/order/:orderId',
        marketingCalendar: '/ordering/marketing-calendar'
    },
    deliveryScheduleSettings: '/delivery-schedule-settings',
    announcements: '/announcements',
    supplierInvoiceExceptions: '/supplier-invoice-exceptions',
    poSoTracking: '/po-so-tracking',
    integrations: '/integrations',
    gmailOAuthCallback: '/integrations/gmail/callback',
    xeroOAuthCallback: '/integrations/xero/callback',
    debugFakeInvoice: '/debug/fake-invoice',
    fallback: '*'
};

/**
 * Represents the base URL for the authentication API.
 * This variable should be used to construct the complete URL for authentication-related requests.
 * It is recommended to append specific endpoints to this base URL when making API requests related to authentication.
 */
const AUTH_API_URL = '/auth';
const LOCATIONS_API_URL = '/locations';
const LOCATION_GROUPS_API_URL = '/location-groups';
const REPORTS_API_URL = '/reports';
const USERS_API_URL = '/users';
const USER_ROLES_API_URL = '/roles';
const FRANCHISE_PARTNERS_API_URL = '/franchise-partners';
const ANNOUNCEMENTS_API_URL = '/announcements';
const SUPPLIER_INVOICE_EXCEPTIONS_API_URL = '/supplier-invoice-exceptions';
const SUPPLIER_INVOICE_ATTACHMENTS_API_URL = '/supplier-invoice-attachments';
const PO_SO_TRACKING_API_URL = '/po-so-tracking';
const GMAIL_OAUTH_API_URL = '/gmail-oauth';
const XERO_OAUTH_API_URL = '/xero-oauth';
const DEBUG_FAKE_INVOICES_API_URL = '/debug/fake-invoices';
const CATEGORY_MANAGEMENT_API_URL = '/category-management';
const PRODUCTS_API_URL = '/products';
const MASTER_SCHEDULES_API_URL = '/schedules/masters';
const SCHEDULES_API_URL = '/schedules';

/**
 * Object containing API routes as key-value pairs.
 * @property {Object} auth - API routes related to authentication.
 * @property {string} auth.googleLogin - Route for Google login redirect uri retrieval.
 */
export const API_ROUTES = {
    auth: {
        googleLogin: `${AUTH_API_URL}/google/auth-redirect-uri`,
        googleCodeExchange: `${AUTH_API_URL}/google/callback`,
        me: `${AUTH_API_URL}/me`,
        logout: `${AUTH_API_URL}/logout`
    },
    locations: {
        getLocations: `${LOCATIONS_API_URL}`,
        getLocation: (pathParams: GetLocationRequestParams) => `${LOCATIONS_API_URL}/${pathParams.location_id}`,
        updateLocation: (pathParams: UpdateLocationRequestParams) => `${LOCATIONS_API_URL}/${pathParams.location_id}`,
        integrationEmploymentHeroBusinesses: (pathParams: GetLocationRequestParams) =>
            `${LOCATIONS_API_URL}/${pathParams.location_id}/integrations/employment-hero/businesses`,
        integrationTandaLocations: (pathParams: GetLocationRequestParams) =>
            `${LOCATIONS_API_URL}/${pathParams.location_id}/integrations/tanda/locations`,
        integrationUnleashedCustomers: (pathParams: GetLocationRequestParams) =>
            `${LOCATIONS_API_URL}/${pathParams.location_id}/integrations/unleashed/customers`,
        categoryDiscounts: (locationId: number | string) => `${LOCATIONS_API_URL}/${locationId}/category-discounts`,
        categoryDiscountProducts: (locationId: number | string, oomCategoryId: string) =>
            `${LOCATIONS_API_URL}/${locationId}/category-discounts/${oomCategoryId}/products`
    },
    integration_health: {
        franchisePartnerHealth: (franchisePartnerId: string) => `/integration-health/franchise-partners/${franchisePartnerId}`,
        platformHealth: '/integration-health/platform'
    },
    franchise_partners: {
        createFranchisePartner: `${FRANCHISE_PARTNERS_API_URL}`,
        getFranchisePartners: `${FRANCHISE_PARTNERS_API_URL}`,
        updateFranchisePartner: (pathParams: UpdateFranchisePartnerRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}`,
        getFranchisePartner: (pathParams: GetFranchisePartnerRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}`,
        deleteFranchisePartner: (pathParams: DeleteFranchisePartnerRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}`,
        getLocations: (pathParams: GetFranchisePartnerLocationsRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}${LOCATIONS_API_URL}`,
        getLocation: (pathParams: GetFranchisePartnerLocationRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}${LOCATIONS_API_URL}/${pathParams.location_id}`,
        updateLocation: (pathParams: UpdateFranchisePartnerLocationRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}${LOCATIONS_API_URL}/${pathParams.location_id}`,
        getUsers: (pathParams: GetFranchisePartnerUsersRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}${USERS_API_URL}`,
        consolidatedReport: (pathParams: GetFranchisePartnerConsolidatedReportRequestParams) =>
            `${FRANCHISE_PARTNERS_API_URL}/${pathParams.franchise_partner_id}/report/${pathParams.location_id}`,
        aggregatedReportByFilter: `${FRANCHISE_PARTNERS_API_URL}/report-aggregated-by-filter`,
        createUser: `${FRANCHISE_PARTNERS_API_URL}${USERS_API_URL}`,
        updateUser: (params: UpdateFranchisePartnerUserRequest) => `${FRANCHISE_PARTNERS_API_URL}${USERS_API_URL}/${params.userId}`,
        deleteUser: (pathParams: DeleteFranchisePartnerUserRequest) => `${FRANCHISE_PARTNERS_API_URL}${USERS_API_URL}/${pathParams.userId}`,
        createCustomExpense: (franchise_partner_id: string) => `${FRANCHISE_PARTNERS_API_URL}/${franchise_partner_id}/custom-expenses`,
        updateCustomExpense: (franchise_partner_id: string, expense_id: string) =>
            `${FRANCHISE_PARTNERS_API_URL}/${franchise_partner_id}/custom-expenses/${expense_id}`,
        deleteCustomExpense: (franchise_partner_id: string, expense_id: string) =>
            `${FRANCHISE_PARTNERS_API_URL}/${franchise_partner_id}/custom-expenses/${expense_id}`
    },
    reports: {
        dailyReport: (pathParams: GetDailyReportRequest) =>
            `${REPORTS_API_URL}/${pathParams.location_id}/daily/${pathParams.prediction_date}`,
        consolidatedReport: (pathParams: GetConsolidatedReportRequest) => `${REPORTS_API_URL}/${pathParams.location_id}`,
        operationalPLReport: () => `${REPORTS_API_URL}/operational-pl`
    },
    dashboard: {
        getDashboard: `/dashboard`
    },
    orders_dashboard: {
        getDashboard: '/orders-dashboard'
    },
    users: {
        getUsers: `${USERS_API_URL}`,
        createUser: `${USERS_API_URL}`,
        updateUser: (pathParams: UpdateUserRequestParams) => `${USERS_API_URL}/${pathParams.userId}`,
        getUser: (pathParams: GetUserRequestParams) => `${USERS_API_URL}/${pathParams.userId}`,
        deleteUser: (pathParams: DeleteUserRequestParams) => `${USERS_API_URL}/${pathParams.userId}`,
        getUserRoles: `${USER_ROLES_API_URL}`
    },
    products: {
        getAllIngredients: `${PRODUCTS_API_URL}/ingredients/all`,
        getAllProductsWithIngredients: `${PRODUCTS_API_URL}/ingredients`,
        createIngredient: `${PRODUCTS_API_URL}/ingredients`,
        updateIngredient: (pathParams: UpdateIngredientRequestParams) => `${PRODUCTS_API_URL}/ingredients/${pathParams.ingredient_id}`,
        deleteIngredient: (pathParams: DeleteIngredientRequestParams) => `${PRODUCTS_API_URL}/ingredients/${pathParams.ingredient_id}`
    },
    location_groups: {
        getLocationGroups: `${LOCATION_GROUPS_API_URL}`,
        getLocationGroup: (pathParams: GetLocationGroupRequestParams) => `${LOCATION_GROUPS_API_URL}/${pathParams.location_group_id}`,
        createLocationGroup: `${LOCATION_GROUPS_API_URL}`,
        updateLocationGroup: (pathParams: UpdateLocationGroupRequestParams) => `${LOCATION_GROUPS_API_URL}/${pathParams.location_group_id}`,
        deleteLocationGroup: (pathParams: DeleteLocationGroupRequestParams) => `${LOCATION_GROUPS_API_URL}/${pathParams.location_group_id}`
    },
    core_products: {
        getCoreProducts: (franchise_partner_id: string) => `${PRODUCTS_API_URL}/core-products/franchise-partner/${franchise_partner_id}`,
        updateCoreProduct: (franchise_partner_id: string) => `${PRODUCTS_API_URL}/core-products/franchise-partner/${franchise_partner_id}`
    },
    ordering: {
        getProducts: '/unleashed-products',
        getSuppliers: '/unleashed-suppliers',
        getDeliveryAddresses: '/unleashed-delivery-addresses',
        submitOrder: '/v2/orders',
        getOrderById: (orderId: string) => `/orders/${orderId}`,
        updateOrderDeliveryDate: (orderId: string) => `/orders/${orderId}/delivery-date`,
        updateOrderDeliveryAddress: (orderId: string) => `/orders/${orderId}/delivery-address`,
        updateOrderLine: (orderId: string, lineId: string) => `/orders/${orderId}/lines/${lineId}`,
        addOrderLine: (orderId: string) => `/orders/${orderId}/lines`,
        replaceOrderLines: (orderId: string) => `/orders/${orderId}/lines`,
        deleteOrderLine: (orderId: string, lineId: string) => `/orders/${orderId}/lines/${lineId}`,
        forceSyncProducts: '/unleashed-products/force-synchronize',
        createAcknowledgement: '/acknowledgements'
    },
    receipting: {
        list: '/receipting',
        getById: (recordId: string) => `/receipting/${recordId}`,
        saveDraft: (recordId: string) => `/receipting/${recordId}/save-draft`,
        confirm: (recordId: string) => `/receipting/${recordId}/confirm`,
        uploadLinePhoto: (recordId: string, lineId: string) => `/receipting/${recordId}/lines/${lineId}/photo-upload`
    },
    credits: {
        create: '/credits'
    },
    marketing: {
        list: '/marketing-calendar'
    },
    delivery_schedules: {
        getMasters: MASTER_SCHEDULES_API_URL,
        createMaster: MASTER_SCHEDULES_API_URL,
        getEffective: (pathParams: { location_id: number | string }) =>
            `${SCHEDULES_API_URL}/effective?location-id=${pathParams.location_id}`,
        createLocationCustomised: `${SCHEDULES_API_URL}/location-customised`,
        updateSchedule: (pathParams: DeleteDeliveryScheduleRequestParams) => `${SCHEDULES_API_URL}/${pathParams.schedule_id}`,
        deleteSchedule: (pathParams: DeleteDeliveryScheduleRequestParams) => `${SCHEDULES_API_URL}/${pathParams.schedule_id}`
    },
    announcements: {
        list: (includeInactive = true) => (includeInactive ? ANNOUNCEMENTS_API_URL : `${ANNOUNCEMENTS_API_URL}?include-inactive=false`),
        active: (onDate?: string) => (onDate ? `${ANNOUNCEMENTS_API_URL}/active?on-date=${onDate}` : `${ANNOUNCEMENTS_API_URL}/active`),
        get: (id: string) => `${ANNOUNCEMENTS_API_URL}/${id}`,
        create: ANNOUNCEMENTS_API_URL,
        patch: (id: string) => `${ANNOUNCEMENTS_API_URL}/${id}`
    },
    supplierInvoiceExceptions: {
        list: SUPPLIER_INVOICE_EXCEPTIONS_API_URL,
        get: (id: string) => `${SUPPLIER_INVOICE_EXCEPTIONS_API_URL}/${id}`,
        analytics: `${SUPPLIER_INVOICE_EXCEPTIONS_API_URL}/analytics`
    },
    supplierInvoiceAttachments: {
        preview: (id: string) => `${SUPPLIER_INVOICE_ATTACHMENTS_API_URL}/${id}/preview`
    },
    poSoTracking: {
        list: PO_SO_TRACKING_API_URL,
        get: (id: string) => `${PO_SO_TRACKING_API_URL}/${id}`,
        close: (id: string) => `${PO_SO_TRACKING_API_URL}/${id}/close`,
        sync: `${PO_SO_TRACKING_API_URL}/sync`,
        syncStatus: `${PO_SO_TRACKING_API_URL}/sync/status`
    },
    debugFakeInvoices: {
        draft: DEBUG_FAKE_INVOICES_API_URL,
        pdf: `${DEBUG_FAKE_INVOICES_API_URL}/pdf`
    },
    gmailOAuth: {
        status: `${GMAIL_OAUTH_API_URL}/status`,
        authRedirectUri: `${GMAIL_OAUTH_API_URL}/auth-redirect-uri`,
        callback: `${GMAIL_OAUTH_API_URL}/callback`,
        disconnect: GMAIL_OAUTH_API_URL
    },
    xeroOAuth: {
        status: `${XERO_OAUTH_API_URL}/status`,
        authRedirectUri: `${XERO_OAUTH_API_URL}/auth-redirect-uri`,
        callback: `${XERO_OAUTH_API_URL}/callback`,
        disconnect: XERO_OAUTH_API_URL
    },
    category_management: {
        snapshot: (unleashedAuthId?: string) =>
            unleashedAuthId
                ? `${CATEGORY_MANAGEMENT_API_URL}/snapshot?unleashed-auth-id=${encodeURIComponent(unleashedAuthId)}`
                : `${CATEGORY_MANAGEMENT_API_URL}/snapshot`,
        createCategory: `${CATEGORY_MANAGEMENT_API_URL}/categories`,
        updateCategory: (categoryId: string) => `${CATEGORY_MANAGEMENT_API_URL}/categories/${categoryId}`,
        updateCategoryStateEmails: (categoryId: string) => `${CATEGORY_MANAGEMENT_API_URL}/categories/${categoryId}/state-emails`,
        deleteCategory: (categoryId: string) => `${CATEGORY_MANAGEMENT_API_URL}/categories/${categoryId}`,
        createSubcategory: (categoryId: string) => `${CATEGORY_MANAGEMENT_API_URL}/categories/${categoryId}/subcategories`,
        deleteSubcategory: (subcategoryId: string) => `${CATEGORY_MANAGEMENT_API_URL}/subcategories/${subcategoryId}`,
        upsertAssignment: (productGuid: string) => `${CATEGORY_MANAGEMENT_API_URL}/assignments/${encodeURIComponent(productGuid)}`,
        upsertOrderConstraint: (productGuid: string) =>
            `${CATEGORY_MANAGEMENT_API_URL}/products/${encodeURIComponent(productGuid)}/order-constraints`
    }
};
